import { denormalisedResponseEntities } from '../../util/data';
import { decodeLatLngBounds } from '../../util/urlHelpers';

export const listingsPerPage = 12;

export const SET_LISTINGS = 'app/DynamicPage/SET_LISTINGS';
export const SET_BOUNDS = 'app/DynamicPage/SET_BOUNDS';
export const SET_LOADING_LISTINGS = 'app/DynamicPage/SET_LOADING_LISTINGS';
export const SET_LOADING_BOUNDS = 'app/DynamicPage/SET_LOADING_BOUNDS';
export const RESET_LISTINGS = 'app/DynamicPage/RESET_LISTINGS';

export const resetListings = () => ({ type: RESET_LISTINGS });

const initialState = {
  listings: [],
  meta: {},
  bounds: null,
  loadingListings: false,
  loadingBounds: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_LISTINGS:
      return { ...state, ...payload };
    case SET_BOUNDS:
      return { ...state, ...payload };
    case SET_LOADING_LISTINGS:
      return { ...state, ...payload };
    case SET_LOADING_BOUNDS:
      return { ...state, ...payload };
    case RESET_LISTINGS:
      return { ...initialState };
    default:
      return state;
  }
}

// Keep the query in sync with SearchPage.duck.js so destination pages return
// the same set of listings as a /s search (minus user-applied filters).
// Note: `mapSearch` is intentionally NOT included — SearchPage strips it before
// hitting the SDK; sending it as an unknown param risks the API ignoring or
// rejecting the call entirely.
const defaultFetchParams = {
  page: 1,
  sort: 'pub_promote',
  perPage: listingsPerPage,
  // Only return listings that are in stock (matches SearchPage's default).
  minStock: 1,
  stockMode: 'match-undefined',
  include: ['author', 'images'],
  'fields.listing': [
    'title',
    'createdAt',
    'geolocation',
    'price',
    'publicData.listingType',
    'publicData.transactionProcessAlias',
    'publicData.unitType',
    'publicData.promoteCommission',
    'publicData.location',
    'publicData.supercap',
    'publicData.ourPick',
    'publicData.instantBook',
    'publicData.promote',
    'publicData.promotedOn',
    'publicData.WR',
    'publicData.pickupEnabled',
    'publicData.shippingEnabled',
    'publicData.mainListingId',
    'publicData.isLocationVariant',
    'publicData.partner',
    'publicData.isRentalTideListing',
    'publicData.fareHarbor',
    'metadata.fareHarbor',
  ],
  'fields.user': [
    'profile.displayName',
    'profile.abbreviatedName',
    'profile.metadata.hostBookingCount',
    'profile.metadata.hostConversationCount',
    'profile.metadata.hostReviewCount',
    'profile.metadata.hostAverageRating',
  ],
  'fields.image': [
    'variants.scaled-small',
    'variants.scaled-medium',
    'variants.listing-card',
    'variants.listing-card-2x',
  ],
  'imageVariant.listing-card': 'w:400;h:300;fit:crop',
  'imageVariant.listing-card-2x': 'w:800;h:600;fit:crop',
  'limit.images': 1,
};

// Mirror SearchPage's listing-type filter so we only return listings of
// the types this marketplace actually supports.
const listingTypeParams = config => {
  const enforce = config?.listing?.enforceValidListingType;
  const types = config?.listing?.listingTypes || [];
  return enforce && types.length
    ? { pub_listingType: types.map(t => t.listingType) }
    : {};
};

// Stateless equivalent of fetchListings: returns the denormalised listing array
// directly instead of writing to redux. Used by CityPage so cross-navigation
// state (e.g. switching cities) never leaks stale listings between pages.
export const queryListingsForCity = (params, config) => async (dispatch, getState, sdk) => {
  const mergedParams = {
    ...defaultFetchParams,
    ...listingTypeParams(config),
    ...params,
  };
  const result = await sdk.listings.query(mergedParams);
  return {
    listings: denormalisedResponseEntities(result),
    meta: result.data.meta,
  };
};

export const fetchListings = (params, config) => async (dispatch, getState, sdk) => {
  dispatch({ type: SET_LOADING_LISTINGS, payload: { loadingListings: true } });
  try {
    const mergedParams = {
      ...defaultFetchParams,
      ...listingTypeParams(config),
      ...params,
    };

    const result = await sdk.listings.query(mergedParams);

    const listings = denormalisedResponseEntities(result);

    const payload = { listings, meta: result.data.meta };

    dispatch({
      type: SET_LISTINGS,
      payload: payload,
    });

    return payload;
  } catch (error) {
    console.error('Error fetching listings:', error);
    throw new Error('Error fetching listings');
  } finally {
    dispatch({ type: SET_LOADING_LISTINGS, payload: { loadingListings: false } });
  }
};

export const setBounds = (pathname, config) => async dispatch => {
  dispatch({ type: SET_LOADING_BOUNDS, payload: { loadingBounds: true } });
  try {
    const listingsLocation = pathname
      .toLowerCase()
      .replace('/destinations/', '')
      .replace('/boat-rentals', '');
    const boundingBox = await getBoundingBox(listingsLocation, config.maps.mapboxAccessToken);

    const bounds = decodeLatLngBounds(boundingBox.join(','));

    dispatch({
      type: SET_BOUNDS,
      payload: { bounds },
    });

    return bounds;
  } catch (error) {
    console.error('Error setting bounds:', error);
    return null;
  } finally {
    dispatch({ type: SET_LOADING_BOUNDS, payload: { loadingBounds: false } });
  }
};

// Server-side loadData: skipped because bounds lookup requires the request URL
// and the location-specific data is rendered synchronously by DynamicPage from
// react-router's location. Listings are fetched client-side from DynamicPageListings.
export const loadData = () => async () => {
  return Promise.resolve();
};

const getBoundingBox = async (location, accessToken) => {
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${location}.json?access_token=${accessToken}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch location data');
    }
    const data = await response.json();
    return data.features[0].bbox.reverse();
  } catch (error) {
    console.error('Error fetching bounding box:', error);
    throw new Error('No results found');
  }
};
